import { snowplowTypes } from 'scripts/lib/analytics/snowplow';
import uiTracker from 'scripts/presentation/components/lib/track';
import fullstory from 'scripts/lib/fullstory';

const UI_EVENT_TO_SNOWPLOW_MAP = {
  'Component Clicked': snowplowTypes.EVENT_COMPONENT_CLICKED,
};

/**
 * Analytics class that exposes methods for working with analytics events.  This is the preferred way of working with analyitcs events.
 *
 * @example
 * import analytics from 'scripts/lib/analytics';
 *
 * analytics.track('my event', { some: 'data' });
 */
class Analytics {
  constructor() {
    this.isAnonymousUser = true;
    this.segmentKey = null;
    this.orgId = null;
    this.gladlyStage = null;
    this.gladlySite = null;
    this.snowplow = null;
  }

  configure({ window, key, snowplowEndpoint }) {
    // reference window as segment annoyingly
    // replaces window.analytics on load
    this.win = window;
    this.segmentKey = key;
    this.gladlyStage = {
      production1: 'production',
      gladly: 'staging',
      staging: 'staging',
      master: 'staging',
      development: 'development',
    }[window.gladlyConfig.cluster];
    this.gladlySite = {
      production1: 'us-1.gladly.com',
      gladly: 'us-uat.gladly.qa',
      staging: 'us-staging.gladly.qa',
      master: 'us-master.gladly.qa',
      development: 'localhost',
    }[window.gladlyConfig.cluster];

    if (snowplowEndpoint) {
      // Initialize snowplow tracker
      window.gladly_snowplow('newTracker', 'cf', snowplowEndpoint, {
        appId: 'agentDesktop',
        forceSecureTracker: !(window.gladlyConfig.cluster === 'development'),
      });
      this.snowplow = window.gladly_snowplow;
    }
  }

  get segment() {
    return this.win.analytics;
  }

  start() {
    if (!this.segmentKey) {
      return;
    }
    this.load(this.segmentKey);
  }

  identifyUser(user) {
    if (!user) {
      return;
    }
    this.identify(user.id, {
      name: user.name,
      email: user.email,
    });

    // Snowplow tracking
    this.snowplow && this.snowplow('setUserId', user.id);
    this.isAnonymousUser = false;

    // Fullstory tracking
    fullstory.activate();
  }

  identifyUserTraits(traits) {
    this.identify(traits);
  }

  identifyOrgId(orgId) {
    if (!orgId) {
      return;
    }
    this.orgId = orgId;
    this.group(orgId);
    this.identify({
      orgId,
    });
  }

  load(...args) {
    this.segment.load && this.segment.load(...args);
  }

  // https://segment.com/docs/sources/website/analytics.js/#identify
  identify(...args) {
    this.segment.identify(...args);
  }

  // https://segment.com/docs/sources/website/analytics.js/#track
  track(...args) {
    !this.isAnonymousUser && this.segment.track(...args);
  }

  trackSnowplow(eventSchema, data = {}, contexts = []) {
    if (!this.snowplow || !this.orgId) return;
    this.snowplow(
      'trackUnstructEvent',
      {
        schema: eventSchema,
        data,
      },
      [
        {
          schema: snowplowTypes.CONTEXT_GLADLY,
          data: {
            orgId: this.orgId,
            stage: this.gladlyStage,
            site: this.gladlySite,
          },
        },
        ...contexts,
      ]
    );
  }

  // https://segment.com/docs/sources/website/analytics.js/#page
  page(...args) {
    if (this.isAnonymousUser) return;
    this.segment.page(...args);
  }

  // https://segment.com/docs/sources/website/analytics.js/#group
  group(...args) {
    this.segment.group(...args);
  }

  // https://segment.com/docs/sources/website/analytics.js/#reset
  reset(...args) {
    this.segment.reset(...args);
    this.isAnonymousUser = true;
    fullstory.deactivate();
  }
}

const analytics = new Analytics();
uiTracker.configure((event, meta) => {
  analytics.trackSnowplow(UI_EVENT_TO_SNOWPLOW_MAP[event], meta);
});

export default analytics;
